import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  api, formatDate, formatMoney, relativeDue, stateOf, stateMeta, strengthMeta, fulfilledLabel,
} from "../lib/api";
import { toast } from "sonner";
import {
  Check, Ban, Quote, Clock, Building2, User2, X, CalendarPlus, BellPlus,
  Mail, ShieldQuestion, RotateCcw, Activity,
} from "lucide-react";
import { FollowUpDialog } from "./FollowUpDialog";
import { CalendarConfirmDialog } from "./CalendarConfirmDialog";

const Badge = ({ meta }) => (
  <span
    className="inline-flex items-center px-2 py-[3px] font-mono text-[10px] uppercase tracking-wider rounded-sm"
    style={{ backgroundColor: meta.bg, color: meta.fg }}
  >
    {meta.label}
  </span>
);

const InfoBlock = ({ icon: Icon, label, children }) => (
  <div className="bg-[#FAFAF9] border border-ledger-border rounded-sm p-3">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    {children}
  </div>
);

const ACTION_LABELS = {
  accepted: "Accepted into ledger",
  follow_up_drafted: "Follow-up drafted",
  reminder_set: "Reminder set",
  calendar_added: "Demo calendar event created",
  fulfilled_with_evidence: "Possible fulfilment confirmed",
  status_fulfilled: "Marked fulfilled",
  status_open: "Reopened",
  status_dismissed: "Dismissed",
  status_conditional: "Marked conditional",
  amount_edited: "Amount edited",
  revised: "Expected date revised",
  blocked: "Marked blocked",
  cancelled: "Marked cancelled",
  partial_payment: "Partial payment recorded",
  outstanding_changed: "Outstanding amount changed",
  remaining_expected: "Remaining amount scheduled",
  condition_satisfied: "Condition satisfied",
  condition_failed: "Condition failed",
  revised_noop: "Revision reviewed — no field change",
  delayed_noop: "Delay reviewed — no field change",
};

const buildTimeline = (c) => {
  const events = [];
  if (c.created_at) {
    events.push({ at: c.created_at, label: "Detected from conversation" });
  }
  (c.action_log || []).forEach((a) => {
    events.push({
      at: a.at,
      label: ACTION_LABELS[a.type] || a.type.replace(/_/g, " "),
      detail: a.detail || a.title || a.at_time || a.value,
    });
  });
  (c.status_history || []).forEach((h) => {
    if (!["fulfilled", "dismissed", "open"].includes(h.to)) {
      events.push({ at: h.at, label: `Status: ${h.from} → ${h.to}` });
    }
  });
  events.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return events;
};

export const DetailDialog = ({ commitmentId, open, onOpenChange, onChanged }) => {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [reminderAt, setReminderAt] = useState("");

  useEffect(() => {
    if (!open || !commitmentId) return;
    setData(null);
    api.getCommitment(commitmentId).then((res) => {
      setData(res);
      setNotes(res.commitment.notes || "");
      setReminderAt((res.commitment.reminder_at || "").slice(0, 10));
    });
  }, [open, commitmentId]);

  const c = data?.commitment;
  const convo = data?.conversation;
  const ffConvo = data?.fulfillment_conversation;
  const state = c ? stateOf(c) : "open";

  const update = async (patch) => {
    if (!c) return;
    setSaving(true);
    try {
      await api.updateCommitment(c.id, patch);
      const fresh = await api.getCommitment(c.id);
      setData(fresh);
      onChanged?.();
      toast.success("Updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const highlightEvidence = (fullText, quote) => {
    if (!fullText || !quote) return fullText || "";
    const i = fullText.indexOf(quote);
    if (i === -1) return fullText;
    return (
      <>
        {fullText.slice(0, i)}
        <mark className="bg-ledger-goldBg text-[#78350F] px-0.5 rounded-sm">
          {fullText.slice(i, i + quote.length)}
        </mark>
        {fullText.slice(i + quote.length)}
      </>
    );
  };

  const canAddCal = c && c.direction === "by_me" && c.status === "open";
  const canFollowUp = c && c.direction === "to_me" && c.status === "open";
  const timeline = c ? buildTimeline(c) : [];
  const fLabel = c ? fulfilledLabel(c) : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="detail-dialog"
          className="max-w-4xl max-h-[90vh] overflow-y-auto bg-ledger-surface border border-ledger-border p-0 gap-0"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Commitment detail</DialogTitle>
            <DialogDescription>Full context and actions for this commitment.</DialogDescription>
          </DialogHeader>
          {!c && (
            <div className="p-16 text-center text-sm text-ledger-muted font-mono">Loading…</div>
          )}
          {c && (
            <>
              <div className="px-8 pt-8 pb-6 border-b border-ledger-border">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge meta={stateMeta[state] || stateMeta.open} />
                    {c.strength !== "strong" && c.strength !== state && (
                      <Badge meta={strengthMeta[c.strength] || strengthMeta.strong} />
                    )}
                    <span className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted">
                      {c.direction === "to_me" ? "Promised to me" : "Promised by me"}
                    </span>
                    {c.source_type === "gmail" && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-ledger-muted font-mono">
                        <Mail className="h-3 w-3" /> from gmail (demo)
                      </span>
                    )}
                  </div>
                  <button
                    data-testid="detail-close"
                    onClick={() => onOpenChange(false)}
                    className="text-ledger-muted hover:text-ledger-ink"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ledger-ink" data-testid="detail-promise">
                  {c.promise_text}
                </h2>

                {c.status === "fulfilled" && fLabel && (
                  <div
                    data-testid="detail-fulfilled-label"
                    className="mt-3 inline-flex items-center gap-2 bg-[#D1FAE5] border border-[#10B981] px-3 py-1.5 rounded-sm"
                  >
                    <Check className="h-4 w-4 text-[#047857]" />
                    <span className="font-mono text-sm text-[#047857]">{fLabel}</span>
                    {c.fulfilled_at && (
                      <span className="font-mono text-[11px] text-ledger-muted">
                        · {formatDate(c.fulfilled_at)}
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InfoBlock icon={User2} label="Person">
                    <div className="font-sans text-sm text-ledger-ink">
                      {c.direction === "to_me" ? c.promisor_name : c.promisee_name}
                    </div>
                  </InfoBlock>
                  <InfoBlock icon={Building2} label="Organization">
                    <div className="font-sans text-sm text-ledger-ink">
                      {c.promisor_organization || "—"}
                    </div>
                  </InfoBlock>
                  <InfoBlock icon={Clock} label={c.status === "fulfilled" ? "Fulfilled" : "Expected"}>
                    {c.status === "fulfilled" && c.fulfilled_at ? (
                      <>
                        <div className="font-mono text-sm text-ledger-ink">
                          {formatDate(c.fulfilled_at)}
                        </div>
                        {c.expected_completion && (
                          <div className="text-[11px] font-mono text-ledger-muted mt-0.5">
                            expected {formatDate(c.expected_completion)}
                          </div>
                        )}
                      </>
                    ) : c.expected_completion ? (
                      <>
                        <div className="font-mono text-sm text-ledger-ink">
                          {formatDate(c.expected_completion)}
                        </div>
                        <div className="text-[11px] font-mono text-ledger-muted mt-0.5">
                          {relativeDue(c.expected_completion)}
                          {c.deadline_certainty === "inferred" && " · inferred"}
                        </div>
                      </>
                    ) : c.timeframe_text ? (
                      <div className="inline-flex items-center gap-1 font-mono text-sm text-ledger-sub">
                        <ShieldQuestion className="h-3.5 w-3.5" /> {c.timeframe_text}
                      </div>
                    ) : (
                      <div className="font-mono text-sm text-ledger-muted">—</div>
                    )}
                  </InfoBlock>
                </div>

                {c.condition && (
                  <div
                    data-testid="detail-condition"
                    className="mt-4 bg-[#F5F3FF] border border-[#DDD6FE] rounded-sm px-4 py-3"
                  >
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#6D28D9] mb-1">
                      Conditional on
                    </div>
                    <div className="font-sans text-sm text-[#4C1D95]">{c.condition}</div>
                  </div>
                )}

                {c.amount != null && (
                  <div
                    data-testid="detail-amount"
                    className={
                      "mt-4 inline-flex items-center gap-3 px-4 py-2 rounded-sm border " +
                      (c.status === "fulfilled"
                        ? "bg-[#D1FAE5] border-[#10B981]"
                        : "bg-ledger-goldBg border-ledger-goldBorder")
                    }
                  >
                    <span className={"text-[11px] uppercase tracking-[0.18em] " + (c.status === "fulfilled" ? "text-[#047857]" : "text-[#92400E]")}>
                      {c.status === "fulfilled" ? (c.direction === "to_me" ? "Recovered" : "Paid") : "Financial commitment"}
                    </span>
                    <span className={"font-mono text-xl " + (c.status === "fulfilled" ? "text-[#047857]" : "text-[#78350F]")}>
                      {formatMoney(c.amount, c.currency)}
                    </span>
                    <span className={"font-mono text-[10px] uppercase " + (c.status === "fulfilled" ? "text-[#047857]" : "text-[#92400E]")}>
                      {c.payment_direction}
                    </span>
                  </div>
                )}

                {c.status === "partially_fulfilled" && (c.original_amount != null || c.amount != null) && (
                  <div
                    data-testid="detail-partial-breakdown"
                    className="mt-4 bg-[#EFF6FF] border border-[#3B82F6] rounded-sm p-4"
                  >
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#1D4ED8] mb-2">
                      Partial fulfilment
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ledger-muted">Original amount</div>
                        <div className="text-ledger-ink text-lg" data-testid="detail-partial-original">
                          {formatMoney(c.original_amount ?? c.amount, c.currency)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ledger-muted">Fulfilled</div>
                        <div className="text-[#047857] text-lg" data-testid="detail-partial-fulfilled">
                          {formatMoney(c.fulfilled_amount ?? 0, c.currency)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ledger-muted">Outstanding</div>
                        <div className="text-[#B45309] text-lg" data-testid="detail-partial-outstanding">
                          {formatMoney(c.outstanding_amount ?? ((c.original_amount ?? c.amount ?? 0) - (c.fulfilled_amount ?? 0)), c.currency)}
                        </div>
                      </div>
                    </div>
                    {(c.remaining_deadline_phrase || c.remaining_expected_completion) && (
                      <div className="mt-3 font-mono text-[12px] text-ledger-sub" data-testid="detail-partial-remaining">
                        Remaining expected: {c.remaining_expected_completion ? formatDate(c.remaining_expected_completion) : ""}
                        {c.remaining_deadline_phrase && (
                          <> {c.remaining_expected_completion ? "· " : ""}“{c.remaining_deadline_phrase}”</>
                        )}
                        {c.remaining_deadline_certainty && (
                          <span className="text-ledger-muted"> — {c.remaining_deadline_certainty}</span>
                        )}
                      </div>
                    )}
                    {(c.partial_fulfilment_events || []).length > 0 && (
                      <div className="mt-4">
                        <div className="text-[10px] uppercase tracking-wider text-ledger-muted mb-1">
                          Partial fulfilment evidence
                        </div>
                        <ul className="space-y-2" data-testid="detail-partial-events">
                          {(c.partial_fulfilment_events || []).map((ev) => (
                            <li key={ev.id} className="bg-white border border-ledger-border rounded-sm p-2.5">
                              <div className="font-mono text-[12px] text-ledger-ink">
                                {formatMoney(ev.amount, ev.currency || c.currency)} on {formatDate(ev.at)}
                              </div>
                              <div className="font-mono text-[11px] text-ledger-muted">
                                outstanding {formatMoney(ev.previous_outstanding_amount, ev.currency || c.currency)} → {formatMoney(ev.new_outstanding_amount, ev.currency || c.currency)}
                              </div>
                              {ev.evidence && (
                                <blockquote className="mt-1 border-l-2 border-[#3B82F6] pl-2 font-serif italic text-[12px] text-ledger-sub">
                                  &ldquo;{ev.evidence}&rdquo;
                                </blockquote>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {(c.revision_history || []).length > 0 && (
                  <div
                    data-testid="detail-revision-history"
                    className="mt-4 bg-[#FFFBEB] border border-[#F59E0B] rounded-sm p-4"
                  >
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#92400E] mb-2">
                      Revision history
                    </div>
                    <ul className="space-y-2">
                      {(c.revision_history || []).map((rh, i) => (
                        <li key={i} className="font-mono text-[12px] text-ledger-sub">
                          <div className="text-ledger-ink">
                            Expected date {rh.from ? `from ${formatDate(rh.from)}` : rh.from_phrase ? `from “${rh.from_phrase}”` : ""}{" "}
                            {rh.to ? `to ${formatDate(rh.to)}` : rh.to_phrase ? `to “${rh.to_phrase}”` : rh.withdrawn ? "withdrawn — awaiting revision" : ""}
                            {rh.certainty ? <span className="text-ledger-muted"> — {rh.certainty}</span> : null}
                          </div>
                          {rh.evidence && (
                            <blockquote className="mt-1 border-l-2 border-[#F59E0B] pl-2 font-serif italic text-[12px] text-ledger-sub">
                              &ldquo;{rh.evidence}&rdquo;
                            </blockquote>
                          )}
                          <div className="text-[11px] text-ledger-muted">{formatDate(rh.at)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="px-8 py-6 border-b border-ledger-border">
                <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-3 inline-flex items-center gap-2">
                  <Quote className="h-3.5 w-3.5" /> Original evidence
                </div>
                <blockquote
                  data-testid="detail-evidence"
                  className="border-l-4 border-ledger-goldBorder bg-ledger-goldBg/50 pl-4 py-3 font-serif italic text-ledger-ink text-lg"
                >
                  &ldquo;{c.evidence_quote}&rdquo;
                </blockquote>
                <div className="mt-2 font-mono text-[11px] text-ledger-muted">
                  Detected {formatDate(c.promised_at)} · confidence {Math.round((c.confidence || 0) * 100)}%
                </div>
              </div>

              {c.fulfillment_evidence && (
                <div className="px-8 py-6 border-b border-ledger-border">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#047857] mb-3 inline-flex items-center gap-2">
                    <Check className="h-3.5 w-3.5" /> Fulfilment evidence
                  </div>
                  <blockquote
                    data-testid="detail-fulfilment-evidence"
                    className="border-l-4 border-[#10B981] bg-[#D1FAE5]/40 pl-4 py-3 font-serif italic text-ledger-ink text-lg"
                  >
                    &ldquo;{c.fulfillment_evidence}&rdquo;
                  </blockquote>
                </div>
              )}

              {convo && convo.source_type !== "seed" && (
                <div className="px-8 py-6 border-b border-ledger-border">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-3">
                    Original conversation {convo.source_type === "gmail" && "· from Gmail (demo)"}
                  </div>
                  <div className="bg-[#FAFAF9] border border-ledger-border rounded-sm p-4 font-mono text-[12.5px] leading-6 text-ledger-sub whitespace-pre-wrap max-h-72 overflow-auto">
                    {highlightEvidence(convo.content, c.evidence_quote)}
                  </div>
                </div>
              )}

              {ffConvo && (
                <div className="px-8 py-6 border-b border-ledger-border">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-3">
                    Fulfilment conversation
                  </div>
                  <div className="bg-[#F0FDF4] border border-[#10B981] rounded-sm p-4 font-mono text-[12.5px] leading-6 text-ledger-sub whitespace-pre-wrap max-h-60 overflow-auto">
                    {highlightEvidence(ffConvo.content, c.fulfillment_evidence)}
                  </div>
                </div>
              )}

              {timeline.length > 0 && (
                <div className="px-8 py-6 border-b border-ledger-border">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-3 inline-flex items-center gap-2">
                    <Activity className="h-3.5 w-3.5" /> Activity timeline
                  </div>
                  <ol
                    data-testid="detail-timeline"
                    className="relative border-l border-ledger-border pl-4 space-y-3"
                  >
                    {timeline.map((e, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-ledger-ink" />
                        <div className="font-sans text-sm text-ledger-ink">{e.label}</div>
                        <div className="font-mono text-[11px] text-ledger-muted">
                          {formatDate(e.at)}
                          {e.detail && <span> · {e.detail}</span>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="px-8 py-6 border-b border-ledger-border grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-2">Notes</div>
                  <textarea
                    data-testid="detail-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => notes !== (c.notes || "") && update({ notes })}
                    rows={3}
                    placeholder="Add private context, reminders, or follow-up ideas…"
                    className="w-full bg-white border border-ledger-border rounded-sm p-3 font-sans text-sm placeholder:text-ledger-muted focus:outline-none focus:ring-2 focus:ring-ledger-ink"
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-2">
                    Remind me if not fulfilled
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={reminderAt}
                      onChange={(e) => setReminderAt(e.target.value)}
                      data-testid="detail-reminder-date"
                      className="flex-1 bg-white border border-ledger-border rounded-sm px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ledger-ink"
                    />
                    <button
                      onClick={() => update({ reminder_at: reminderAt || null })}
                      data-testid="detail-reminder-save"
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-sm border border-ledger-border text-xs text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink transition-colors duration-150"
                    >
                      <BellPlus className="h-4 w-4" /> Set
                    </button>
                  </div>
                  {c.reminder_at && (
                    <div className="mt-1 font-mono text-[11px] text-ledger-muted">
                      active reminder: {formatDate(c.reminder_at)}
                    </div>
                  )}
                </div>
              </div>

              <div className="px-8 py-5 flex items-center justify-between flex-wrap gap-3">
                <div className="text-[11px] font-mono text-ledger-muted">ID {c.id.slice(0, 8)}</div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {c.status !== "dismissed" && c.status !== "fulfilled" && (
                    <button
                      data-testid="detail-dismiss"
                      onClick={() => update({ status: "dismissed" })}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-ledger-border text-sm text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink transition-colors duration-150"
                    >
                      <Ban className="h-4 w-4" /> Dismiss
                    </button>
                  )}
                  {canFollowUp && (
                    <button
                      data-testid="detail-followup"
                      onClick={() => setFollowOpen(true)}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-ledger-border text-sm text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink transition-colors duration-150"
                    >
                      <Mail className="h-4 w-4" /> Draft follow-up
                    </button>
                  )}
                  {canAddCal && (
                    <button
                      data-testid="detail-calendar"
                      onClick={() => setCalOpen(true)}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-ledger-border text-sm text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink transition-colors duration-150"
                    >
                      <CalendarPlus className="h-4 w-4" /> Demo calendar event
                    </button>
                  )}
                  {c.status !== "fulfilled" && (
                    <button
                      data-testid="detail-fulfill"
                      onClick={() => update({ status: "fulfilled" })}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#047857] text-white text-sm font-medium hover:bg-[#036344] transition-colors duration-200"
                    >
                      <Check className="h-4 w-4" /> Mark fulfilled
                    </button>
                  )}
                  {(c.status === "fulfilled" || c.status === "dismissed") && (
                    <button
                      data-testid="detail-reopen"
                      onClick={() => update({ status: "open" })}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-ledger-border text-sm text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink transition-colors duration-150"
                    >
                      <RotateCcw className="h-4 w-4" /> Reopen
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <FollowUpDialog open={followOpen} onOpenChange={setFollowOpen} commitment={c} />
      <CalendarConfirmDialog
        open={calOpen}
        onOpenChange={setCalOpen}
        commitment={c}
        onDone={() => {
          api.getCommitment(c.id).then(setData);
          onChanged?.();
        }}
      />
    </>
  );
};
